import { ApiRequestError } from '../api/client.js';

/**
 * READING THE SERVER'S RENDER-SAFETY REFUSAL, FOR A REVIEWER RATHER THAN FOR AN OPERATOR (lane U, 2026-09-26).
 *
 * `QueueService.applyReviewAction` re-measures the text being approved and, when a blocking finding is
 * present and the request did not acknowledge it, answers 409 with
 * `{ code: 'RENDER_SAFETY_ISSUES', renderSafety: { issues: [...] } }`. That measurement — taken at the
 * moment of the decision, on the exact bytes being approved — is what the review screen shows. The report
 * stored at import can be stale (a rule changed, or the reviewer edited the text since), so it is never
 * used to decide what to ask.
 */
export interface RenderRefusalFinding {
  /** Where in the question, in words: "the question text", "feedback step 3", "answer option 2". */
  place: string;
  /** What the check thinks will go wrong, in words. */
  problem: string;
  /** The stored text around the finding, verbatim. */
  sample: string;
  /** The server's reason id, kept for anyone reading the page source; not shown as the explanation. */
  reason: string;
}

/**
 * The one reason the server treats as NOT blocking (`isBlockingRenderSafetyIssue` in
 * `packages/api/src/import/render-preflight.ts`). It rides along in a refusal's report, and presenting it
 * as a reason he was stopped would be false. If the server ever changes its predicate, the failure mode of
 * this copy is to show one line too many — never to hide a finding that blocked him.
 */
const NON_BLOCKING_REASON = 'normalizer-quarantine';

const PROBLEM_BY_REASON: Readonly<Record<string, string>> = {
  'visible-tag-text': 'contains HTML the check does not recognise, which may show up as raw tags',
  'markdown-emphasis-in-raw-html': 'has Markdown inside HTML, which shows up as raw symbols such as ** or [ ]( )',
  'literal-emphasis-delimiter': 'has ** that will show up literally instead of as bold',
  'dangerous-markup': 'contains script-like markup, which is unsafe and will not display as written',
};

/**
 * `null` when the failure is anything other than a render-safety refusal — the caller shows it as it
 * always did. An array (possibly empty, if the report could not be read) when it is one: the refusal is
 * real either way, and the screen must offer the acknowledgement rather than a dead end.
 */
export function readRenderSafetyRefusal(
  reason: unknown,
  patch: Record<string, unknown> | null,
): RenderRefusalFinding[] | null {
  if (!(reason instanceof ApiRequestError) || reason.status !== 409) return null;
  const details = reason.details;
  if (!isRecord(details) || details.code !== 'RENDER_SAFETY_ISSUES') return null;
  const report = details.renderSafety;
  const issues = isRecord(report) && Array.isArray(report.issues) ? report.issues : [];
  return issues
    .filter(isRecord)
    .filter((issue) => issue.reason !== NON_BLOCKING_REASON)
    .map((issue) => {
      const reasonId = typeof issue.reason === 'string' ? issue.reason : 'unknown';
      return {
        place: describePlace(issue, patch),
        problem: PROBLEM_BY_REASON[reasonId] ?? `was flagged by the render check (${reasonId})`,
        sample: typeof issue.sample === 'string' ? issue.sample : '',
        reason: reasonId,
      };
    });
}

function describePlace(issue: Record<string, unknown>, patch: Record<string, unknown> | null): string {
  const fieldPath = typeof issue.fieldPath === 'string' ? issue.fieldPath : '';
  const stepMatch = /^steps\[(\d+)\]\./.exec(fieldPath);
  if (stepMatch) {
    const steps = Array.isArray(patch?.steps) ? patch.steps : [];
    const step: unknown = steps[Number(stepMatch[1])];
    const stepId = isRecord(step) ? step.id : issue.stepId;
    // A practice question's root step IS the question; every other step is the feedback an answer routes to.
    if (isRecord(step) && step.is_root === true) return 'the question text';
    const shownId = typeof stepId === 'number' || typeof stepId === 'string' ? stepId : Number(stepMatch[1]) + 1;
    return `feedback step ${shownId}`;
  }
  const responseMatch = /^responses\[(\d+)\]\./.exec(fieldPath);
  if (responseMatch) return `answer option ${Number(responseMatch[1]) + 1}`;
  if (fieldPath === 'title') return 'the title';
  if (typeof issue.label === 'string' && issue.label) return issue.label;
  return fieldPath || 'the question';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
